import {
  industriesCreateMutation,
  industriesDestroyMutation,
  industriesListOptions,
  industriesListQueryKey,
  industriesPartialUpdateMutation,
  industriesRetrieveOptions,
  IndustryReadable
} from '@crm/api'
import { ConfirmModal } from '@crm/components'
import {
  FormModal,
  FormNomenclatureDefaultItem
} from '@crm/features/nomenclatures/components'
import { useAppNavigate } from '@crm/hooks/useAppNavigate'
import { useTableSorter } from '@crm/hooks/useTableSorter'
import { paths } from '@crm/routes'
import { defaultNonPagination } from '@crm/utils'
import { DayLabel, TableActions, TableContainer } from '@shared/components'
import {
  useInvalidatePredicateQueries,
  useQueryParams,
  useResetPagination
} from '@shared/hooks'
import { useLocales } from '@shared/locales'
import { showApiErrors } from '@shared/utils'
import { useMutation, useQuery } from '@tanstack/react-query'
import { App, Button } from 'antd'
import { Add, Edit, Shop, Trash } from 'iconsax-react'
import { useCallback, useMemo, useState } from 'react'

import type { MenuProps, TableColumnsType } from 'antd'

const formName = 'industries'

export const NomenclaturesIndustries = () => {
  const { t } = useLocales()
  const params = useQueryParams()
  const sorter = useTableSorter()
  const navigate = useAppNavigate()

  const { notification } = App.useApp()
  const { resetPagination } = useResetPagination()
  const { invalidateQueries } = useInvalidatePredicateQueries()

  const [isCreate, setIsCreate] = useState(false)
  const [editItemId, setEditItemId] = useState<number>()
  const [deleteItemId, setDeleteItemId] = useState<number>()

  const { data: activeDomains, isLoading } = useQuery(
    industriesListOptions({ query: { ...defaultNonPagination, ...params } })
  )

  const { mutate } = useMutation({
    ...industriesDestroyMutation(),
    onError: (e) => showApiErrors(e),
    onSuccess: () => {
      invalidateQueries([industriesListQueryKey()])
      resetPagination()
      setDeleteItemId(undefined)
      notification.success({
        message: t('success.success'),
        description: t('success.success_data_delete')
      })
    }
  })

  const onDelete = () => {
    if (deleteItemId) {
      mutate({ path: { id: deleteItemId } })
    }
  }

  const onCancelDelete = () => setDeleteItemId(undefined)

  const onCloseModal = () => {
    setEditItemId(undefined)
    setIsCreate(false)
  }

  const getActionColumnItem: (id: number) => MenuProps['items'] = useCallback(
    (id) => [
      {
        label: t('nomenclature.edit'),
        key: 'edit',
        icon: <Edit size={20} />,
        onClick: () => setEditItemId(id)
      },
      {
        label: t('nomenclature.edit_sub_industries'),
        key: 'edit_sub_industries',
        icon: <Shop size={20} />,
        onClick: () => navigate(`/nomenclatures/industries/${id}`)
      },
      { type: 'divider' },
      {
        label: t('nomenclature.delete'),
        key: 'delete',
        icon: <Trash size={20} />,
        danger: true,
        onClick: () => setDeleteItemId(id)
      }
    ],
    [navigate, t]
  )

  const columns: TableColumnsType<IndustryReadable> = useMemo(
    () => [
      {
        title: t('nomenclature.name'),
        dataIndex: 'name',
        key: 'name',
        sorter: true
      },
      {
        title: t('filter.created_at'),
        dataIndex: 'created_at',
        key: 'created_at',
        sorter: true,
        render: (created_at) => <DayLabel date={created_at} />
      },
      {
        title: t('lead.modified_at'),
        dataIndex: 'modified_at',
        key: 'modified_at',
        sorter: true,
        render: (modified_at) => <DayLabel date={modified_at} />
      },
      {
        width: 50,
        fixed: 'right',
        key: 'actions',
        render: ({ id }) => <TableActions options={getActionColumnItem(id)} />
      }
    ],
    [t, getActionColumnItem]
  )

  return (
    <>
      <TableContainer
        data={activeDomains}
        columns={columns}
        loading={isLoading}
        rightChildren={
          <Button
            type="primary"
            onClick={() => setIsCreate(true)}
            icon={<Add size={20} />}
          >
            {t('nomenclature.add_industry')}
          </Button>
        }
        onChange={(_, __, result) => sorter(result)}
        onRow={(data) => ({
          onClick: () => {
            navigate(
              paths.admin.nomenclatures.general.industry(String(data.id))
            )
          }
        })}
        rowClassName="pointer"
        bordered
      />

      <ConfirmModal
        open={!!deleteItemId}
        onSuccess={onDelete}
        onCancel={onCancelDelete}
      />

      <FormModal
        id={editItemId}
        formName={formName}
        open={!!(editItemId || isCreate)}
        getItem={industriesRetrieveOptions}
        onCreate={industriesCreateMutation}
        onUpdate={industriesPartialUpdateMutation}
        onCloseModal={onCloseModal}
      >
        <FormNomenclatureDefaultItem />
      </FormModal>
    </>
  )
}
